import * as XLSX from "xlsx";
import { DEFAULT_EXPORT_FILE_NAME } from "./constants";
import { formatExportValue } from "./export-utils";
import type {
  ExportTableCell,
  ExportTableColumn,
  ExportTableModel,
  XlsxExportOptions,
  XlsxExportResult,
} from "./export.types";

const DEFAULT_SHEET_NAME = "Sheet1";
const DEFAULT_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DEFAULT_COLUMN_WIDTH_PIXELS = 75;

export class XlsxExportService {
  serialize(table: ExportTableModel, options: XlsxExportOptions = {}): XlsxExportResult {
    const sheetName = options.sheetName ?? DEFAULT_SHEET_NAME;
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(this._buildSheetData(table));

    worksheet["!cols"] = this._alignColumnWidths(table.columns);

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    return {
      workbook,
      fileName: options.fileName ?? `${DEFAULT_EXPORT_FILE_NAME}.xlsx`,
      mimeType: DEFAULT_MIME_TYPE,
      sheetName,
    };
  }

  createBlob(result: XlsxExportResult): Blob {
    const content = XLSX.write(result.workbook, {
      bookType: "xlsx",
      cellStyles: true,
      type: "array",
    });

    return new Blob([content], { type: result.mimeType });
  }

  private _buildSheetData(table: ExportTableModel): string[][] {
    return [
      table.columns.map((column) => column.headerText),
      ...table.rows.map((row) => row.cells.map((cell) => this._formatCell(cell))),
    ];
  }

  private _alignColumnWidths(columns: ExportTableColumn[]): XLSX.ColInfo[] {
    return columns.map((column) => {
      if (typeof column.width === "number") {
        return { wpx: column.width };
      }

      return { wpx: DEFAULT_COLUMN_WIDTH_PIXELS };
    });
  }

  private _formatCell(cell: ExportTableCell): string {
    return formatExportValue(cell.formattedValue ?? cell.value);
  }
}

const xlsxExportService = new XlsxExportService();

export { xlsxExportService };
